import { maskVendor } from "@/shared/lib/vendorMask";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Crosshair, Download, Loader2, Radar } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";

import { geoService, parseReport } from "../api/geoService";
import { stripForeignEngines } from "../model/domesticEngines";
import { useCompliance } from "@/contexts/AuthContext";
import { exportReportHtml } from "../lib/exportReportHtml";
import { GEO_ETA_TEXT } from "../model/geoCopy";
import { useGeoRunPolling } from "../model/useGeoRunPolling";
import { GeoReportPanel } from "./components/GeoReportPanel";
import {
  GeoReportV2Panel,
  V2_ANCHORS,
  v2SectionAvailability,
} from "./components/report2/GeoReportV2Panel";
import { errorText } from "@/shared/lib/apiErrors";

/** v2 报告根节点 id ——「导出 HTML」按这个 id 定位要 clone 的容器(只带走报告本身,
 * 不带外层返回键/锚点导航——那些是本页交互 chrome,不是报告内容)。 */
const V2_REPORT_ROOT_ID = "geo-report-v2-root";

/** legacy 锚点(旧报告 JSON 无 score_breakdown 时回落旧面板)。 */
const LEGACY_ANCHORS: Array<[string, string]> = [
  ["总览", "geo-sec-hero"],
  ["五维", "geo-sec-radar"],
  ["检索归因", "geo-sec-search"],
  ["引擎矩阵", "geo-sec-matrix"],
  ["原话墙", "geo-sec-probes"],
  ["引用与阵地", "geo-sec-presence"],
  ["竞品与缺口", "geo-sec-comp"],
  ["策略", "geo-sec-strategy"],
];

function fmtTime(iso?: string): string {
  if (!iso) return "";
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]} ${m[2]}` : iso;
}

/**
 * 声量雷达报告详情页(/geo-radar/runs/:id)。
 * S1:新报告走 12 板块版式(实习生版式全保留+徽标全链);旧报告回落 legacy 面板。
 * 顶栏可手动改指基线(仅 SUCCESS;后端事务内清旧立新)。
 */
export default function GeoReportDetailPage() {
  const { id } = useParams();
  const runId = id ? Number(id) : null;
  const { run, error } = useGeoRunPolling(Number.isFinite(runId) ? runId : null);
  const queryClient = useQueryClient();
  const { domesticOnly } = useCompliance();
  const [repointing, setRepointing] = useState(false);
  const [repointErr, setRepointErr] = useState<string | null>(null);
  // 改指成功后的本地即时态(详情轮询 hook 非 react-query,不吃 invalidate)
  const [repointedOk, setRepointedOk] = useState(false);

  // 较上轮 delta:取列表里比当前更早的最近一次 SUCCESS 分数(legacy 面板用)
  const runsQuery = useQuery({ queryKey: ["geo", "runs"], queryFn: () => geoService.listRuns(20) });
  const prevScore = (() => {
    const runs = runsQuery.data ?? [];
    const idx = runs.findIndex((r) => r.id === runId);
    if (idx < 0) return null;
    return (
      runs.slice(idx + 1).find((r) => r.status === "SUCCESS" && r.overallScore != null)
        ?.overallScore ?? null
    );
  })();
  const isBaseline =
    repointedOk ||
    run?.baseline ||
    (runsQuery.data ?? []).find((r) => r.id === runId)?.baseline ||
    false;

  const parsedReport = run && run.status === "SUCCESS" ? parseReport(run.reportJson) : null;
  // 1011 domesticOnly:报告不显示 ChatGPT(旧报告兜底;导出 HTML 同用这份)
  const report = parsedReport && domesticOnly ? stripForeignEngines(parsedReport) : parsedReport;
  const isV2 = Boolean(report?.score_breakdown?.components?.length);

  const repoint = async () => {
    if (!runId) return;
    setRepointing(true);
    setRepointErr(null);
    try {
      await geoService.repointBaseline(runId);
      setRepointedOk(true);
      await queryClient.invalidateQueries({ queryKey: ["geo", "runs"] });
    } catch (e: unknown) {
      setRepointErr(errorText(e));
    } finally {
      setRepointing(false);
    }
  };

  // 导出 HTML(所见即所得):clone v2 报告根节点 + 收集同源 CSS,走 Blob 下载——
  // 只对 v2 报告开放(legacy 报告没有这个根节点,Hero 里也不会渲染这颗按钮)。
  const handleExportHtml = () => {
    if (!report) return;
    const el = document.getElementById(V2_REPORT_ROOT_ID);
    if (!el) return;
    exportReportHtml(el, { brand: report.brand });
  };

  // codex S1 P2:缺席板块的锚点不进目录(点了没反应的死链接)
  const v2Avail = report && isV2 ? v2SectionAvailability(report) : {};
  const anchors: Array<[string, string]> = isV2
    ? V2_ANCHORS.filter(([, , target]) => v2Avail[target] !== false).map(([no, label, target]) => [
        `${no} ${label}`,
        target,
      ])
    : LEGACY_ANCHORS;

  return (
    <div className="space-y-4">
      {isV2 ? (
        // v2 报告:品牌/总分/导出/设为基线都已收进新版 Hero(report2/ReportHero.tsx)里,
        // 这里只留返回键,避免跟 Hero 里的大标题信息重复。
        <div className="flex items-center gap-3" data-print-hide>
          <Link
            to="/geo-radar"
            className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-sm text-muted-foreground hover:bg-muted"
          >
            <ArrowLeft className="h-4 w-4" /> 返回
          </Link>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/geo-radar"
            className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-sm text-muted-foreground hover:bg-muted"
          >
            <ArrowLeft className="h-4 w-4" /> 返回
          </Link>
          <Radar className="h-5 w-5 text-primary" />
          <h1 className="text-xl font-semibold">{run?.brand ?? "GEO雷达报告"}</h1>
          {run?.status === "SUCCESS" && run.overallScore != null && (
            <Badge className="bg-emerald-500 text-white tabular-nums">
              {run.overallScore} · {run.grade ?? ""}
            </Badge>
          )}
          {isBaseline && (
            <Badge variant="outline" data-testid="geo-baseline-badge">
              基线
            </Badge>
          )}
          <span className="text-sm text-muted-foreground">{fmtTime(run?.createdAt)}</span>
          <span className="ml-auto flex items-center gap-2">
            {/* 下载报告(2026-07-29 测试反馈):走浏览器打印 → 另存为 PDF。
                零后端改动,图表/雷达图原样带走;`print:` 变体在 index.css 里把导航/按钮藏掉、
                把折叠段展开。要「带我方 logo 的正式交付版」再上后端出 PDF。 */}
            {run?.status === "SUCCESS" && report && (
              <Button
                variant="outline"
                size="sm"
                data-testid="geo-download-report"
                onClick={() => window.print()}
              >
                <Download className="mr-1 h-3.5 w-3.5" />
                下载报告
              </Button>
            )}
            {run?.status === "SUCCESS" && !isBaseline && (
              <Button
                variant="outline"
                size="sm"
                data-testid="geo-repoint-baseline"
                disabled={repointing}
                onClick={() => void repoint()}
              >
                {repointing ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Crosshair className="mr-1 h-3.5 w-3.5" />
                )}
                设为基线
              </Button>
            )}
          </span>
        </div>
      )}
      {repointErr && (
        <div className="rounded-md bg-rose-50 px-4 py-2 text-sm text-rose-700">
          设为基线失败:{repointErr}
        </div>
      )}

      {report && (
        <nav
          data-testid="geo-anchor-nav"
          className="sticky top-0 z-10 -mx-1 flex gap-1 overflow-x-auto rounded-md border bg-background/95 px-1 py-1.5 backdrop-blur"
        >
          {anchors.map(([label, target]) => (
            <button
              key={target}
              className="whitespace-nowrap rounded-md px-3 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={() =>
                document.getElementById(target)?.scrollIntoView({ behavior: "smooth" })
              }
            >
              {label}
            </button>
          ))}
        </nav>
      )}

      {error && (
        <Card>
          <CardContent className="p-6 text-sm text-rose-600">报告加载失败:{error}</CardContent>
        </Card>
      )}
      {run?.status === "RUNNING" && (
        <Card>
          <CardContent className="flex items-center gap-3 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" /> 体检中(联网检索 20 问 + 生态推演,
            {GEO_ETA_TEXT})…
          </CardContent>
        </Card>
      )}
      {run?.status === "FAILED" && (
        <Card>
          <CardContent className="p-6 text-sm text-rose-600">
            体检失败:{maskVendor(run.errorMessage) ?? "未知错误"}
          </CardContent>
        </Card>
      )}
      {report &&
        (isV2 ? (
          <GeoReportV2Panel
            report={report}
            createdAt={run?.createdAt}
            rootId={V2_REPORT_ROOT_ID}
            isBaseline={isBaseline}
            onExportHtml={handleExportHtml}
            onExportPdf={() => window.print()}
            onRepointBaseline={() => void repoint()}
            repointing={repointing}
          />
        ) : (
          <GeoReportPanel report={report} prevScore={prevScore} />
        ))}
    </div>
  );
}
